import React from 'react'
import PropTypes from 'prop-types'
import { GatsbyImage } from 'gatsby-plugin-image'

// Components
import StandardImage from './StandardImage'
import ReactImgix from './ReactImgix'

// Utils
import { chooseImageComponent, getImgSrc } from './utils'

const Image = (props) => {
  const {
    aspectRatio,
    backgroundColor,
    gAs,
    gLoading,
    height,
    image,
    imgClassName,
    imgStyle,
    loading,
    objectFit,
    objectPosition,
    role,
    sizes,
    src,
    width,
    wrapperClassName,
    wrapperStyle,
    img,
  } = props

  const imageSource = getImgSrc(src, image, width, height)
  const imageType = chooseImageComponent(imageSource, img)

  // If a prismic or alt from another source is not populated, defaultProps will not be used. Instead, the null value will come through.
  let { alt } = props
  if (alt == null) alt = 'Fallback description of image'

  const commonProps = {
    alt,
    imgStyle,
    backgroundColor,
    objectFit,
    objectPosition,
  }

  const imgixAndStandardCommonProps = {
    aspectRatio,
    height,
    width,
    role,
    wrapperStyle,
    imgClassName,
    src: imageSource,
    wrapperClassName,
  }

  // console.log('👾 Image', props)
  return (
    <>
      {imageType === 'gatsbyImage' && (
        <GatsbyImage
          image={imageSource}
          style={wrapperStyle}
          className={`${wrapperClassName} imageWrapper`}
          imgClassName={`${imgClassName} image`}
          loading={gLoading}
          as={gAs}
          draggable="false"
          loading={loading}
          {...commonProps}
        />
      )}
      {imageType === 'imgix' && (
        <ReactImgix sizes={sizes} {...commonProps} {...imgixAndStandardCommonProps} />
      )}
      {imageType === 'standard' && (
        <StandardImage {...commonProps} {...imgixAndStandardCommonProps} />
      )}
    </>
  )
}

export default Image

Image.propTypes = {
  alt: PropTypes.string,
  aspectRatio: PropTypes.number,
  backgroundColor: PropTypes.string,
  gAs: PropTypes.string, // GatsbyImage only -> The HTML element used for the outer wrapper.
  gLoading: PropTypes.oneOf(['eager', 'lazy']), // GatsbyImage only -> Loading behavior for the image. You should set this to "eager" for above-the-fold images to ensure they start loading before React hydration.
  image: (props, propName, componentName) => {
    if (!props.src && !props.image)
      return new Error(`Either src or image must be pass as a prop to ${componentName}`)
    if (!props.src && typeof props[propName] !== 'string' && typeof props[propName] !== 'object')
      return new Error(
        `Hey, you should pass a string or an object for ${propName} in ${componentName} but you passed a ${typeof props[
          propName
        ]} ya dummy`
      )
  },
  height: PropTypes.number,
  imgClassName: PropTypes.string, // All images -> CSS class applied to the <img> element.
  imgStyle: PropTypes.object, // All images -> Inline styles applied to the <img> element.
  loading: PropTypes.string, // For GatsbyImage only, either 'lazy' or 'eager'
  objectFit: PropTypes.oneOf(['cover', 'contain', 'fill', 'none', 'scale-down']),
  objectPosition: PropTypes.string, // Guide (https://bit.ly/3pFzXQ6)
  role: PropTypes.oneOf(['img', 'presentation']),
  sizes: PropTypes.string,
  src: (props, propName, componentName) => {
    if (props.src && props.image)
      return new Error(
        `Either src or image must be pass as a prop to ${componentName}, but not both`
      )
    if (!props.src && !props.image)
      return new Error(`Either src or image must be pass as a prop to ${componentName}`)
    if (!props.image && typeof props[propName] !== 'string')
      return new Error(
        `Hey, you should pass a string for ${propName} in ${componentName} but you passed a ${typeof props[
          propName
        ]} ya dummy`
      )
  },
  width: PropTypes.number,
  wrapperClassName: PropTypes.string, // All images -> CSS class applied to the outer wrapper.
  wrapperStyle: PropTypes.object, // All images -> Inline styles applied to the outer wrapper.
}

Image.defaultProps = {
  alt: 'Fallback description of image',
  aspectRatio: null,
  backgroundColor: 'transparent',
  gAs: 'div',
  gLoading: 'lazy',
  height: null,
  imgClassName: '',
  imgStyle: {},
  loading: 'lazy',
  objectFit: 'cover',
  objectPosition: '50% 50%',
  role: 'img',
  sizes: '100vw',
  width: null,
  wrapperClassName: '',
  wrapperStyle: {},
}
